import { createSlice } from "@reduxjs/toolkit";
import {
  createSousProfile,
  fetchSousProfiles,
  fetchSousProfileById,
  addSousProfileEmail,
  updateSousProfile,
  deleteSousProfile,
  verifyEmancipationToken,
  setEmancipationPassword,
  switchSousProfile,
  restoreMainProfile,
} from "../thunks/sousProfileThunks";

const mainActingSession = {
  mode: "main",
  accessToken: null,
  actingAs: null,
  profile: null,
};

const pickSousProfileId = (value) =>
  value?.id || value?._id || value?.subProfileId || value?.subprofileId || null;

const normalizeActingSession = (payload) => {
  if (!payload || typeof payload !== "object") return mainActingSession;

  const accessToken = payload.accessToken || payload.access_token || null;
  const actingAs =
    payload.actingAs ||
    payload.acting_as ||
    payload.subprofileId ||
    payload.subProfileId ||
    payload.profile?.id ||
    null;

  if (payload.mode === "subprofile" || accessToken || actingAs) {
    const profile = payload.profile || null;
    const profileId = pickSousProfileId(profile) || actingAs;

    return {
      mode: "subprofile",
      accessToken,
      actingAs,
      profile:
        profile && profileId
          ? {
              ...profile,
              id: pickSousProfileId(profile) || profileId,
              subprofileId: profile.subprofileId || profile.subProfileId || profileId,
            }
          : profile,
    };
  }

  return mainActingSession;
};

const loadActingSession = () => {
  if (typeof window === "undefined") return mainActingSession;

  try {
    const raw = window.localStorage.getItem("actingSession");
    if (!raw) return mainActingSession;
    return normalizeActingSession(JSON.parse(raw));
  } catch {
    return mainActingSession;
  }
};

const initialState = {
  items: [],
  byId: {},
  current: null,
  loading: false,
  error: null,
  verifyTokenStatus: "idle",
  verifyTokenError: null,
  setPasswordStatus: "idle",
  setPasswordError: null,

  actingSession: loadActingSession(),
};

const sousProfileSlice = createSlice({
  name: "sousProfiles",
  initialState,
  reducers: {
    clearSousProfilesError(state) {
      state.error = null;
    },
    clearVerifyTokenState(state) {
      state.verifyTokenStatus = "idle";
      state.verifyTokenError = null;
    },
    clearSetPasswordState(state) {
      state.setPasswordStatus = "idle";
      state.setPasswordError = null;
    },
    clearActingSession(state) {
      state.actingSession = mainActingSession;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSousProfiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSousProfiles.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
        state.byId = {};
        state.items.forEach((sp) => {
          if (sp?.id) state.byId[sp.id] = sp;
        });
      })
      .addCase(fetchSousProfiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur récupération sous-profils";
      });

    builder
      .addCase(fetchSousProfileById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSousProfileById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload || null;
        if (state.current?.id) {
          state.byId[state.current.id] = state.current;
        }
      })
      .addCase(fetchSousProfileById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur récupération sous-profil";
      });

    builder
      .addCase(createSousProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSousProfile.fulfilled, (state, action) => {
        state.loading = false;
        const created = action.payload;
        if (created) {
          state.items.push(created);
          if (created.id) {
            state.byId[created.id] = created;
          }
        }
      })
      .addCase(createSousProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur création sous-profil";
      });

    builder
      .addCase(addSousProfileEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSousProfileEmail.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        if (updated?.id) {
          state.byId[updated.id] = updated;
          const idx = state.items.findIndex((s) => s.id === updated.id);
          if (idx !== -1) state.items[idx] = updated;
          if (state.current?.id === updated.id) state.current = updated;
        }
      })
      .addCase(addSousProfileEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur mise à jour email";
      });

    builder
      .addCase(updateSousProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSousProfile.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        if (updated?.id) {
          state.byId[updated.id] = updated;
          const idx = state.items.findIndex((s) => s.id === updated.id);
          if (idx !== -1) state.items[idx] = updated;
          if (state.current?.id === updated.id) state.current = updated;
        }
      })
      .addCase(updateSousProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur mise à jour sous-profil";
      });

    builder
      .addCase(deleteSousProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSousProfile.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload?.id;
        if (!deletedId) return;

        state.items = state.items.filter(
          (sp) => String(pickSousProfileId(sp)) !== String(deletedId)
        );
        delete state.byId[deletedId];

        if (state.current && String(pickSousProfileId(state.current)) === String(deletedId)) {
          state.current = null;
        }

        const actingAs =
          state.actingSession?.actingAs?.id ||
          state.actingSession?.actingAs?._id ||
          state.actingSession?.actingAs ||
          state.actingSession?.profile?.id ||
          state.actingSession?.profile?._id;

        if (actingAs && String(actingAs) === String(deletedId)) {
          state.actingSession = mainActingSession;
        }
      })
      .addCase(deleteSousProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur suppression sous-profil";
      });

    builder
      .addCase(verifyEmancipationToken.pending, (state) => {
        state.verifyTokenStatus = "loading";
        state.verifyTokenError = null;
      })
      .addCase(verifyEmancipationToken.fulfilled, (state) => {
        state.verifyTokenStatus = "succeeded";
      })
      .addCase(verifyEmancipationToken.rejected, (state, action) => {
        state.verifyTokenStatus = "failed";
        state.verifyTokenError = action.payload || "Erreur vérification token";
      });

    builder
      .addCase(setEmancipationPassword.pending, (state) => {
        state.setPasswordStatus = "loading";
        state.setPasswordError = null;
      })
      .addCase(setEmancipationPassword.fulfilled, (state) => {
        state.setPasswordStatus = "succeeded";
      })
      .addCase(setEmancipationPassword.rejected, (state, action) => {
        state.setPasswordStatus = "failed";
        state.setPasswordError = action.payload || "Erreur définition mot de passe";
      });

    builder
      .addCase(switchSousProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(switchSousProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.actingSession = normalizeActingSession(action.payload);
      })
      .addCase(switchSousProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur switch profile";
      });

    builder
      .addCase(restoreMainProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreMainProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.actingSession = {
          mode: "main",
          accessToken: action.payload?.access_token || null,
          actingAs: null,
          profile: action.payload?.profile || null,
        };
      })
      .addCase(restoreMainProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Erreur restore profile";
      });
  },
});

export const {
  clearSousProfilesError,
  clearVerifyTokenState,
  clearSetPasswordState,
  clearActingSession,
} = sousProfileSlice.actions;

export default sousProfileSlice.reducer;
